import { useTranslation } from 'react-i18next';

import { useWeb3AuthContext, Web3AuthSteps } from '@pkg/components';
import { flushTokens, useAppDispatch, useAppSelector, useLogin, useMagicLink, useUpdateUser } from '@pkg/hooks';

import { apiFactory, Auth } from '@app/client';
import { Events } from '@app/client/common/structs/events/events';
import { setError } from '@app/store/actions/error';
import { registerEvent } from '@app/store/actions/events';
import { addNotification } from '@app/store/actions/notification';
import { type ErrorState } from '@app/store/types/error';
import { useUser } from '@app/store/zustand';
import { useFantasyReferralStore } from '@app/store/zustand/FantasyGame/Referrals';

interface RegisterUser {
    email: string;
    password: string;
    subscribed: boolean;
    errorFeedback?: boolean;
    callback?: () => void;
    redirect?: string;
}

export function useRegister() {
    const { t } = useTranslation(['waitlist', 'account']);

    const dispatch = useAppDispatch();
    const partnerConversionId = useAppSelector((rootState) => rootState.partnerIntegrations.partnerConversionId);
    const userId = useAppSelector((rootState) => rootState.user?.userAccount?.user.id);
    const { orderItems } = useAppSelector(({ picker }) => picker);

    const { sendMagicLink } = useMagicLink();
    const { setIsLoading: setIsAccountLoading } = useUser();
    const { mutateAsync: updateUser } = useUpdateUser();
    const { setAuthEmail, setCurrentStep } = useWeb3AuthContext();
    const { sanitizeStorage } = useLogin();

    function successfulRegistration({ email, redirect }: { email: string; redirect?: string }) {
        registerEvent(Events.user_signup);
        sendMagicLink({ email: email.trim(), redirect });
        setCurrentStep(Web3AuthSteps.EmailActivation);
    }

    async function register({ email, password, subscribed, errorFeedback, callback, redirect }: RegisterUser) {
        const { clearCode, referralCode } = useFantasyReferralStore.getState();
        setIsAccountLoading(true);
        apiFactory().ws?.closeWithoutReconnect();

        const hasPendingOrder = orderItems?.length > 0;
        const draftUserId = userId ? userId : undefined;

        try {
            // New user with pending order
            if (hasPendingOrder && draftUserId) {
                const registerUserResponse = await updateUser({ value: { email, password, subscribed }, dispatch });
                setAuthEmail(email);

                if (registerUserResponse?.ok) {
                    successfulRegistration({ email, redirect });
                    callback?.();
                } else {
                    if (registerUserResponse?.fields?.['email']) {
                        dispatch(
                            addNotification({
                                message: t('waitlist_alert_unique'),
                                severity: 'error',
                                duration: 5000,
                            }),
                        );
                    }
                    if (registerUserResponse?.fields?.['password']) {
                        dispatch(
                            addNotification({
                                message: t('waitlist_alert_password'),
                                severity: 'error',
                                duration: 5000,
                            }),
                        );
                    }
                }
            } else {
                const createAccount = await apiFactory().call(Auth.createAccount, {
                    value: {
                        email: email.trim(),
                        password: password.trim(),
                        partner_conversion_id: partnerConversionId,
                        referral_code: referralCode,
                    },
                });

                if (createAccount.ok) {
                    sanitizeStorage();
                    successfulRegistration({ email, redirect });
                    callback?.();
                } else {
                    //     if (createAccount.fields) {
                    //         const firstError: AuthErrorItem = Object.values(signUpResponse.fields)[0][0];
                    //         setErrorMessage(firstError.message);
                    //         setHasAuthError(true);
                    //     }
                    // }

                    const fields = createAccount.fields ? createAccount.fields : undefined;

                    if (errorFeedback) {
                        dispatch(setError({ ...createAccount, notify: true, fields }));
                    }

                    flushTokens();
                }
                setIsAccountLoading(false);
                return createAccount;
            }
        } catch (error) {
            dispatch(setError({ ...(error as ErrorState), notify: true }));
            flushTokens();
            setIsAccountLoading(false);
            throw error;
        } finally {
            clearCode();
        }
    }

    return {
        register,
    };
}
